import { useEffect, useRef } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { AlertCode } from 'shared/types/checkout';
import { setAlert, useAppDispatch } from 'store';

export const CHECKOUT_ALERT_QUERY_PARAM = 'checkoutAlert';

// AlertCodes that a redirect back to checkout is allowed to trigger via the
// checkoutAlert query param. An explicit allowlist means an arbitrary/stale
// query param value can't be used to surface an unrelated alert.
const ALLOWED_QUERY_ALERT_CODES: ReadonlySet<string> = new Set([
  AlertCode.UPI_PAYMENT_FAILED,
]);

/**
 * Some Stripe payment methods (currently UPI) settle via redirect through
 * /payment-return instead of resolving in-page. When that settlement fails
 * in a way the checkout page itself never observes, /payment-return sends
 * the shopper back here with a `checkoutAlert` query param (see
 * PaymentReturnClient) so the same alert an in-page failure would show
 * still surfaces, and the shopper can retry UPI or pick another method
 * without leaving checkout.
 *
 * Call once from each checkout page variant's root component. Strips the
 * query param after consuming it so a refresh or back-navigation doesn't
 * re-trigger the alert.
 */
export const usePendingCheckoutAlert = (): void => {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const hasConsumedRef = useRef(false);

  const alertCode = searchParams?.get(CHECKOUT_ALERT_QUERY_PARAM) ?? null;

  useEffect(() => {
    if (!alertCode || hasConsumedRef.current || !pathname) {
      return;
    }
    hasConsumedRef.current = true;

    if (ALLOWED_QUERY_ALERT_CODES.has(alertCode)) {
      dispatch(setAlert({ code: alertCode as AlertCode }));
    }

    const remainingParams = new URLSearchParams(searchParams?.toString());
    remainingParams.delete(CHECKOUT_ALERT_QUERY_PARAM);
    const query = remainingParams.toString();
    router.replace(query ? `${pathname}?${query}` : pathname);
    // Only ever needs to run once, on mount, for whatever query params the
    // page loaded with — re-running on router/searchParams identity churn
    // would race with the replace() this effect itself just issued.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};
